<script>
import {computed, defineComponent, ref} from 'vue';
import {useRoute, useRouter} from 'vue-router';
import {useI18n} from "vue-i18n";
import {useDonationSessionStore} from "../../stores/donationSession";
import BaseModal from "../components/BaseModal.vue";
import LoadingSpinner from "../components/loadingSpinner.vue";
import Popup from "../components/popup.vue";
import {useAnalyticEventStore} from "../../stores/analyticEvents";
import {useLandingPageStore} from "../../stores/landingPage";
import {useUserStore} from "../../stores/user";
export default defineComponent({
  name: 'StripePaymentButton',
  components: {Popup, LoadingSpinner, BaseModal},
  // components: {PaymentFormError},
  props: {
    redirect: {
      type: Boolean,
      default: true
    },
    enabled: {
      type: Boolean,
      default: true
    }
  },
  emits: ['payment-success', 'click'],

  setup(props, { emit }) {
    // Router
    const route = useRoute();
    const router = useRouter();
    const {t} = useI18n();

    // const organizationId = computed(() => route.params.id);
    const donationSessionStore = useDonationSessionStore();
    const analyticEventStore = useAnalyticEventStore();

    const landingStore = useLandingPageStore();

    const userStore = useUserStore();

    return {
      t,
      donationSessionStore,
      // organizationId,
      router,
      landingStore,
      analyticEventStore,
      userStore
    };
  },

  data() {
    return {
      stripeElements: null,
      showModal: false,
      paymentFormLoaded: false,
      success: false
    };
  },

  computed: {
    donateButtonTextColor() {
      // Use the brand store's method to determine the text color based on the primary color.
      return this.landingStore.getContrastTextColor(this.landingStore.localBrand.color_primary);
    },
    cssProps() {
      return {
        '--button-text': this.donateButtonTextColor
      };
    },
    formError() {
      return this.donationSessionStore.donationSession.errors?.error;
    },
    errorMessage() {
      return this.donationSessionStore.donationSession.errors?.message;
    },
    successMessage() {
      return this.donationSessionStore.donationSession.successMessage;
    },
    userSignedIn() {
      // return this.userStore.user !== null;
      return false;
    }
  },
  methods: {
    async getStripeClient() {
      return await this.donationSessionStore.getStripeClient();
    },

    // Could not figure out how to show card info when user is signed in. Something to look into
    async initPaymentForm() {
      this.showModal = true;
      this.paymentFormLoaded = false;
      // this.analyticEventStore.setOrganizationId(this.organizationId); // had to add this in for the giving page
      this.donationSessionStore.loadPaymentForm(null, this.userSignedIn).then(() => {

        this.getStripeClient().then((loaded) => {
          if(!loaded) return;
          const stripe = this.donationSessionStore.stripeClient;

          const appearance = {
            theme: 'stripe',
          };
          this.stripeElements = stripe?.elements({
              clientSecret: this.donationSessionStore.getClientSecret,
              loader: "always",
              paymentMethodCreation: "manual",
              appearance,
            // fonts,
            // customerSessionClientSecret: show_saved ? customerSessionSecret : null,
            }
          );
          if (this.stripeElements) {
            const el = this.stripeElements.create(
              "payment",
              {
                layout: "accordion",
                // defaultCollapsed: false,
                // radios: true,
                defaultValues: {
                  billingDetails: {
                    email: this.donationSessionStore.donationForm.donorEmail,
                    name: this.donationSessionStore.fullName
                  }
                },
                // paymentMethodOrder: show_saved ? ['card'] : null
              });
            this.paymentFormLoaded = true;
            this.analyticEventStore.logEvent({
              action: `donation-intent-user-conversion`,
              user_id: this.donationSessionStore.currentDonorUserId,
              metadata: {
                user_id: this.donationSessionStore.currentDonorUserId
              }
            });
            el.mount("#stripe-payment-form");
          }
        });
      }).catch((error) => {
        console.error('Stripe payment form load error:', error);
        this.donationSessionStore.donationSession.errors = { message: error?.message || "Something went wrong. Please refresh the page and try again." };
        this.showModal = false;
        this.$refs.popup.show();
      });
    },

    async submitDonation() {
      const {error} = await this.stripeElements.submit();
      if (!error) {
        this.paymentFormLoaded = false;

        await this.donationSessionStore.submitStripePaymentForm(this.stripeElements);
        if(this.formError) {
          // Reloads the payment form to allow for more payment attempts
          this.paymentFormLoaded = true;
        }
        else if (this.donationSessionStore.donationSession.completed) {
          this.success = true;
          this.analyticEventStore.logEvent({
            action: `stripe-payment-button-success`,
            user_id: this.donationSessionStore.currentDonorUserId,
            metadata: {
              data: this.stripeElements
            }
          });
          if(this.redirect) {
            if(this.donationSessionStore.donationSession.redirectUrl){
              setTimeout(() => {
                const userSignedIn = this.donationSessionStore.donationSession.userAuthenticated;
                if (userSignedIn) {
                  const org = this.$route.params.org || this.donationSessionStore.organizationId;
                  window.location.href = `/${org}/donor/dashboard/overview?donated=true`;
                } else {
                  window.location.href = this.donationSessionStore.donationSession.redirectUrl;
                }
              }, 4000);
            } else {
              setTimeout(() => {
                this.router.push({path: '/donate/thank_you'});
              }, 4000);
            }
          }else {
            this.$emit('payment-success');
          }
        }
      }
    }
  }

});
</script>
<template>
  <button
    ref="donateButton"
    class="text-lg font-semibold"
    :disabled="!enabled"
    :style="cssProps"
    @click="$emit('click')"
  >
    Give Now
  </button>

  <!--  PAYMENT MODAL  -->
  <base-modal
    v-if="showModal"
    size="small"
    @close="showModal = false;"
  >
    <div v-if="success || formError">
      <div
        v-if="formError"
        class="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative"
        role="alert"
      >
        <strong class="font-bold mr-10">
          {{ t('payment.errors.error') }}
        </strong>
        <span class="block sm:inline">
          {{ t(errorMessage) }}
        </span>
      </div>

      <div
        v-if="success"
        class="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded relative"
        role="alert"
      >
        {{ successMessage }}
      </div>
    </div>

    <div
      v-show="!success"
      class="min-h-[200px]"
    >
      <loading-spinner
        v-if="!paymentFormLoaded"
        size="small"
        class="m-32"
      />

      <div
        v-show="paymentFormLoaded"
        id="stripe-payment-form"
        ref="paymentForm"
        class="max-w-screen-md min-h-[200px] mb-4"
      />
      <button
        v-show="paymentFormLoaded"
        ref="donateButton"
        :style="cssProps"
        class="border-3 border-pink-400 mb-4"
        @click="submitDonation"
      >
        Donate
      </button>
    </div>
  </base-modal>

  <popup
    ref="popup"
    :message="errorMessage"
    :duration="1400"
  />
</template>
<style lang="scss" scoped>
@import url('https://fonts.googleapis.com/css2?family=Montserrat:ital,wght@0,100..900;1,100..900&display=swap');
button {
  @apply w-full py-2 px-4 rounded mt-2;
  @apply text-default_white active:animate-pulse;

  background-color: var(--button-color);
  color: var(--button-text);
  &:hover {
    background-color: var(--button-hover);
  }

  &:disabled {
    @apply bg-gray-300 text-white;
  }
}

:deep(.circle-progress-fill-line) {
  stroke: var(--button-color) !important;
}

</style>
